import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import Logo from "../components/Logo";
import SEO from "../components/SEO";
import { api } from "../services/api";
import {
  MessageSquare,
  Users,
  Calendar,
  BarChart3,
  Shield,
  Zap,
  Clock,
  CheckCircle,
  Bot,
  ArrowRight,
  Wrench,
} from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

// Get and clear the returnTo URL from sessionStorage
const getReturnToUrl = () => {
  const returnTo = sessionStorage.getItem('returnTo');
  if (returnTo) {
    sessionStorage.removeItem('returnTo');
    return returnTo;
  }
  return '/dashboard';
};

export default function AuthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, checkAuth } = useApp();
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Dev login state (only used in development)
  const [devEmail, setDevEmail] = useState('');
  const [devLoading, setDevLoading] = useState(false);
  const [devError, setDevError] = useState(null);

  // Handle OAuth callback
  useEffect(() => {
    const authSuccess = searchParams.get("auth_success");
    const authError = searchParams.get("auth_error");

    if (authSuccess === "true") {
      checkAuth().then((authenticated) => {
        if (authenticated) {
          // Reset session expired flag after successful login
          api.resetSessionExpiredFlag();
          // Redirect to stored returnTo URL or dashboard
          navigate(getReturnToUrl());
        }
      });
    } else if (authError) {
      setError(decodeURIComponent(authError));
    }
  }, [searchParams, checkAuth, navigate]);

  // Redirect if already authenticated
  useEffect(() => {
    if (user) {
      // Redirect to stored returnTo URL or dashboard
      navigate(getReturnToUrl());
    }
  }, [user, navigate]);

  const handleSignIn = () => {
    setIsLoading(true);
    window.location.href = `${API_URL}/auth/login`;
  };

  // Dev login handler (only works in development)
  const handleDevLogin = async (e) => {
    e.preventDefault();
    setDevLoading(true);
    setDevError(null);

    try {
      const response = await fetch(`${API_URL}/auth/dev-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: devEmail }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Dev login failed');
      }

      // Trigger auth check to update context
      await checkAuth();
      // Reset session expired flag after successful login
      api.resetSessionExpiredFlag();
      // Redirect to stored returnTo URL or dashboard
      navigate(getReturnToUrl());
    } catch (err) {
      setDevError(err.message);
    } finally {
      setDevLoading(false);
    }
  };

  const features = [
    { icon: MessageSquare, text: "Answer customer questions 24/7" },
    { icon: Users, text: "Capture leads automatically" },
    { icon: Calendar, text: "Book appointments via chat" },
    { icon: BarChart3, text: "Track performance with analytics" },
  ];

  return (
    <>
      <SEO
        title={null}
        description="Create intelligent AI agents that handle customer conversations 24/7, capture leads automatically, and book appointments via WhatsApp. No coding required."
        path="/"
      />
      <div className="min-h-screen flex overflow-auto relative" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)' }}>
      {/* Subtle background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Soft gradient orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-30 blur-3xl"
             style={{ background: 'radial-gradient(circle, #c7d2fe 0%, transparent 70%)' }} />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full opacity-20 blur-3xl"
             style={{ background: 'radial-gradient(circle, #ddd6fe 0%, transparent 70%)' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-15 blur-3xl"
             style={{ background: 'radial-gradient(circle, #a5b4fc 0%, transparent 70%)' }} />

        {/* Subtle grid overlay */}
        <div className="absolute inset-0 opacity-[0.03]"
             style={{
               backgroundImage: 'linear-gradient(#6366f1 1px, transparent 1px), linear-gradient(90deg, #6366f1 1px, transparent 1px)',
               backgroundSize: '50px 50px'
             }} />
      </div>

      {/* Left Side - Sign In Form */}
      <div className="w-full lg:w-1/2 flex flex-col p-6 lg:p-8 xl:p-10 relative z-10 min-h-screen lg:min-h-0">
        {/* Logo */}
        <div className="shrink-0">
          <Logo size="small" clickable={false} />
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full py-4 lg:py-6">
          {/* Headline */}
          <h1 className="text-2xl lg:text-3xl xl:text-4xl font-bold mb-3 leading-tight">
            <span style={{ color: '#1e293b' }}>Build </span>
            <span style={{
              background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>AI agents</span>
            <span style={{ color: '#1e293b' }}> for WhatsApp</span>
          </h1>
          <p className="text-sm lg:text-base mb-6" style={{ color: '#64748b' }}>
            Create intelligent AI agents that handle customer conversations,
            capture leads, and book appointments automatically.
          </p>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 rounded-lg"
                 style={{
                   background: 'rgba(239, 68, 68, 0.1)',
                   border: '1px solid rgba(239, 68, 68, 0.3)'
                 }}>
              <p className="text-sm" style={{ color: '#dc2626' }}>{error}</p>
            </div>
          )}

          {/* Sign in button */}
          <button
            onClick={handleSignIn}
            disabled={isLoading}
            className="w-full flex items-center justify-between px-5 py-3.5 rounded-xl
                       transition-all duration-300 group disabled:opacity-50 disabled:cursor-not-allowed
                       hover:scale-[1.02] active:scale-[0.98]"
            style={{
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#6366f1';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e2e8f0';
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)';
            }}
          >
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span className="font-medium" style={{ color: '#1e293b' }}>
                {isLoading ? "Redirecting..." : "Continue with Google"}
              </span>
            </div>
            <ArrowRight className="w-5 h-5 transition-all duration-300 group-hover:translate-x-1" style={{ color: '#6366f1' }} />
          </button>

          {/* Features list */}
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-center gap-2 text-sm"
                style={{ color: '#64748b' }}
              >
                <feature.icon className="w-4 h-4 shrink-0" style={{ color: '#8b5cf6' }} />
                <span>{feature.text}</span>
              </div>
            ))}
          </div>

          {/* Trust indicators */}
          <div className="mt-6 pt-4" style={{ borderTop: '1px solid #e2e8f0' }}>
            <div className="flex items-center gap-4 text-xs" style={{ color: '#94a3b8' }}>
              <div className="flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5" style={{ color: '#22c55e' }} />
                <span>SSL encryption</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5" style={{ color: '#f59e0b' }} />
                <span>No credit card required</span>
              </div>
            </div>
          </div>

          {/* Dev Login - Only visible in development */}
          {process.env.NODE_ENV === 'development' && (
            <div className="mt-6 pt-4" style={{ borderTop: '1px dashed #94a3b8' }}>
              <p className="text-xs flex items-center justify-center gap-1.5 mb-3" style={{ color: '#94a3b8' }}>
                <Wrench className="w-3.5 h-3.5" />
                Development Only
              </p>
              <form onSubmit={handleDevLogin} className="space-y-3">
                <input
                  type="email"
                  value={devEmail}
                  onChange={(e) => setDevEmail(e.target.value)}
                  placeholder="Enter existing user email"
                  className="w-full px-4 py-2.5 rounded-lg text-sm transition-all"
                  style={{
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    color: '#1e293b',
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#6366f1'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  required
                />
                {devError && (
                  <p className="text-xs" style={{ color: '#dc2626' }}>{devError}</p>
                )}
                <button
                  type="submit"
                  disabled={devLoading}
                  className="w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-all disabled:opacity-50"
                  style={{
                    background: '#1e293b',
                    color: '#ffffff',
                  }}
                >
                  {devLoading ? 'Logging in...' : 'Dev Login'}
                </button>
              </form>
            </div>
          )}

          {/* Terms */}
          <p className="mt-4 text-xs" style={{ color: '#94a3b8' }}>
            By signing up, you agree to our{" "}
            <a href="/terms" target="_blank" rel="noopener noreferrer"
               className="hover:underline transition-colors"
               style={{ color: '#6366f1' }}>
              Terms of Service
            </a>{" "}
            and{" "}
            <a href="/privacy" target="_blank" rel="noopener noreferrer"
               className="hover:underline transition-colors"
               style={{ color: '#6366f1' }}>
              Privacy Policy
            </a>
          </p>
        </div>

        {/* Footer */}
        <div className="shrink-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs" style={{ color: '#94a3b8' }}>
          <span>© {new Date().getFullYear()} Botibonga. All rights reserved.</span>
          <div className="flex items-center gap-4">
            <Link to="/features" className="hover:underline transition-colors" style={{ color: '#94a3b8' }}
                  onMouseEnter={(e) => e.target.style.color = '#6366f1'}
                  onMouseLeave={(e) => e.target.style.color = '#94a3b8'}>
              Features
            </Link>
            <Link to="/pricing" className="hover:underline transition-colors" style={{ color: '#94a3b8' }}
                  onMouseEnter={(e) => e.target.style.color = '#6366f1'}
                  onMouseLeave={(e) => e.target.style.color = '#94a3b8'}>
              Pricing
            </Link>
            <Link to="/about" className="hover:underline transition-colors" style={{ color: '#94a3b8' }}
                  onMouseEnter={(e) => e.target.style.color = '#6366f1'}
                  onMouseLeave={(e) => e.target.style.color = '#94a3b8'}>
              About
            </Link>
            <Link to="/contact" className="hover:underline transition-colors" style={{ color: '#94a3b8' }}
                  onMouseEnter={(e) => e.target.style.color = '#6366f1'}
                  onMouseLeave={(e) => e.target.style.color = '#94a3b8'}>
              Contact
            </Link>
          </div>
        </div>
      </div>

      {/* Right Side - Product Preview */}
      <div className="hidden lg:flex w-1/2 flex-col relative overflow-hidden"
           style={{
             borderLeft: '1px solid #e2e8f0',
             background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.03) 0%, rgba(168, 85, 247, 0.03) 100%)'
           }}>
        {/* Decorative accent lines */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-0 w-full h-px"
               style={{ background: 'linear-gradient(90deg, transparent 0%, #6366f1 50%, transparent 100%)', opacity: 0.3 }} />
          <div className="absolute bottom-0 left-0 w-full h-px"
               style={{ background: 'linear-gradient(90deg, transparent 0%, #a855f7 50%, transparent 100%)', opacity: 0.2 }} />
        </div>

        <div className="flex-1 flex flex-col justify-center p-6 xl:p-10 relative">
          {/* Chat Preview Card */}
          <div className="rounded-xl overflow-hidden max-w-md mx-auto w-full"
               style={{
                 background: '#ffffff',
                 border: '1px solid #e2e8f0',
                 boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.1), 0 4px 12px rgba(0, 0, 0, 0.05)'
               }}>
            {/* Chat Header */}
            <div className="px-4 py-3" style={{
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(168, 85, 247, 0.05) 100%)',
              borderBottom: '1px solid #e2e8f0'
            }}>
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full flex items-center justify-center"
                     style={{
                       background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                       boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)'
                     }}>
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm" style={{ color: '#1e293b' }}>AI Agent</h3>
                  <div className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#22c55e' }}></span>
                    <span className="text-xs" style={{ color: '#64748b' }}>Online</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Chat Messages */}
            <div className="p-4 space-y-3 max-h-[280px] overflow-y-auto" style={{ background: '#fafafa' }}>
              {/* User message */}
              <div className="flex justify-end">
                <div className="px-3 py-2 rounded-xl rounded-br-sm max-w-[85%]"
                     style={{
                       background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                       boxShadow: '0 2px 8px rgba(99, 102, 241, 0.2)'
                     }}>
                  <p className="text-sm font-medium text-white">I'd like to book an appointment</p>
                </div>
              </div>

              {/* Bot message */}
              <div className="flex justify-start">
                <div className="px-3 py-2 rounded-xl rounded-bl-sm max-w-[85%]"
                     style={{
                       background: '#ffffff',
                       border: '1px solid #e2e8f0'
                     }}>
                  <p className="text-sm" style={{ color: '#1e293b' }}>Sure! Here are available times:</p>
                  <div className="mt-2 space-y-1.5">
                    <div className="flex items-center gap-2 text-xs rounded-md px-2.5 py-1.5"
                         style={{ background: 'rgba(99, 102, 241, 0.05)', border: '1px solid rgba(99, 102, 241, 0.2)' }}>
                      <Clock className="w-3 h-3" style={{ color: '#6366f1' }} />
                      <span style={{ color: '#1e293b' }}>9:00 AM - 9:30 AM</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs rounded-md px-2.5 py-1.5"
                         style={{ background: 'rgba(99, 102, 241, 0.05)', border: '1px solid rgba(99, 102, 241, 0.2)' }}>
                      <Clock className="w-3 h-3" style={{ color: '#6366f1' }} />
                      <span style={{ color: '#1e293b' }}>2:00 PM - 2:30 PM</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* User message */}
              <div className="flex justify-end">
                <div className="px-3 py-2 rounded-xl rounded-br-sm"
                     style={{
                       background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                       boxShadow: '0 2px 8px rgba(99, 102, 241, 0.2)'
                     }}>
                  <p className="text-sm font-medium text-white">2 PM works!</p>
                </div>
              </div>

              {/* Bot confirmation */}
              <div className="flex justify-start">
                <div className="px-3 py-2 rounded-xl rounded-bl-sm max-w-[85%]"
                     style={{
                       background: '#ffffff',
                       border: '1px solid #e2e8f0'
                     }}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <CheckCircle className="w-3.5 h-3.5" style={{ color: '#22c55e' }} />
                    <span className="text-xs font-medium" style={{ color: '#22c55e' }}>Confirmed!</span>
                  </div>
                  <p className="text-sm" style={{ color: '#1e293b' }}>Booked for tomorrow at 2:00 PM.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-6 grid grid-cols-3 gap-3 max-w-md mx-auto w-full">
            {[
              { value: '24/7', label: 'Available', color: '#6366f1' },
              { value: '90%', label: 'Resolved', color: '#8b5cf6' },
              { value: '10min', label: 'Setup', color: '#a855f7' }
            ].map((stat, index) => (
              <div key={index} className="rounded-lg p-3 text-center"
                   style={{
                     background: '#ffffff',
                     border: '1px solid #e2e8f0',
                     boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                   }}>
                <p className="text-xl font-bold" style={{ color: stat.color }}>{stat.value}</p>
                <p className="text-xs" style={{ color: '#94a3b8' }}>{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Testimonial */}
          <div className="mt-6 max-w-md mx-auto w-full p-4 rounded-lg"
               style={{
                 background: '#ffffff',
                 border: '1px solid #e2e8f0',
                 boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
               }}>
            <blockquote className="italic text-sm" style={{ color: '#64748b' }}>
              "Our AI agent handles 80% of customer inquiries automatically."
            </blockquote>
            <div className="mt-3 flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold"
                   style={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}>
                JK
              </div>
              <div>
                <p className="font-medium text-sm" style={{ color: '#1e293b' }}>James Kimani</p>
                <p className="text-xs" style={{ color: '#94a3b8' }}>Founder, Waski Kenya</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
